import type { ReactNode } from "react";
import {
  BulkActionsBar,
  type BulkAction,
  Box,
  Button,
  DataTableEmpty,
  EditableTitleField,
  Icon,
  Menu,
  MenuPopup,
  MenuTrigger,
  type SelectAllGroup,
  Text,
} from "@salestrak/ui";
import { SelectableMediaTile } from "#app/components/media/media-tiles";
import { useRowSelection } from "#app/hooks/kanban/use-board-selection";
import { formatRelativeDate } from "#app/lib/utils/format-relative-date";
import {
  MEDIA_SOURCES,
  MediaAddedBy,
  type MediaGroupBy,
  type MediaRow,
  mediaRowBuckets,
} from "./issue-media-columns";

const NO_SELECT_ALL_GROUPS: SelectAllGroup[] = [];

function mediaOrigin({ item, origin }: MediaRow): string {
  if (item.source === "form") return item.onFile.form.name;
  return MEDIA_SOURCES[origin].label;
}

export function IssueMediaGrid({
  rows,
  groupBy,
  rowId,
  bulkActions,
  onOpen,
  renderMenu,
  renamingId,
  onRename,
  onRenameEnd,
  emptyMessage,
}: {
  rows: MediaRow[];
  groupBy: MediaGroupBy;
  rowId: (row: MediaRow) => string;
  bulkActions: BulkAction<MediaRow>[];
  onOpen: (index: number) => void;
  renderMenu: (row: MediaRow) => ReactNode;
  renamingId: string | null;
  onRename: (row: MediaRow, name: string) => void;
  onRenameEnd: () => void;
  emptyMessage: string;
}) {
  const { selectedIds, selectedRowData, toggle, clear } = useRowSelection<MediaRow>();
  const buckets = mediaRowBuckets(rows, groupBy);

  return (
    <Box direction="column" fill="flex" minHeight={0} overflow="auto">
      {buckets.length === 0 ? (
        <DataTableEmpty message={emptyMessage} />
      ) : (
        <Box direction="column" gap="lg" paddingTop="md" paddingBottom="lg" paddingX="lg">
          {buckets.map((bucket) => (
            <Box key={bucket.key} direction="column" gap="sm">
              <Text size="sm" weight="medium" variant="muted">
                {bucket.label} · {bucket.data.length}
              </Text>
              <Box
                display="grid"
                gap="md"
                role="list"
                aria-label={bucket.label}
                // 12rem floor so a card keeps its form's name whole.
                className="grid-cols-[repeat(auto-fill,minmax(12rem,1fr))]"
              >
                {bucket.data.map((row) => {
                  const id = rowId(row);
                  const renaming = renamingId === row.photoId;
                  return (
                    <Box key={id} role="listitem">
                      <SelectableMediaTile
                        photoId={row.photoId}
                        contentType={row.item.contentType}
                        label={row.name}
                        editing={renaming}
                        menu={
                          <Menu>
                            <MenuTrigger
                              render={
                                <Button aria-label={`Actions for ${row.name}`} size="icon-sm" variant="outline">
                                  <Icon name="ellipsis" size="md" />
                                </Button>
                              }
                            />
                            <MenuPopup align="end">{renderMenu(row)}</MenuPopup>
                          </Menu>
                        }
                        selected={selectedIds.has(id)}
                        onSelectedChange={
                          bulkActions.length > 0 ? (selected) => toggle(id, row, selected) : undefined
                        }
                        onOpen={() =>
                          selectedIds.size > 0 ? toggle(id, row, !selectedIds.has(id)) : onOpen(row.index)
                        }
                        caption={
                          <Box direction="column" gap="2xs" minWidth={0}>
                            <Box direction="row" align="baseline" gap="sm" justify="between">
                              {renaming ? (
                                // The field's 2px vertical padding bleeds back out so the card keeps its height.
                                <Box direction="row" fill="flex" minWidth={0} marginYNegative="2xs">
                                  <EditableTitleField
                                    aria-label={`Rename ${row.name}`}
                                    editing
                                    fill
                                    flushLeft
                                    chrome="none"
                                    size="sm"
                                    weight="semibold"
                                    value={row.name}
                                    onCommit={(next) => onRename(row, next)}
                                    onEditingChange={(next) => {
                                      if (!next) onRenameEnd();
                                    }}
                                  />
                                </Box>
                              ) : (
                                <Text size="sm" weight="semibold" truncate={1}>
                                  {row.name}
                                </Text>
                              )}
                              {row.date ? (
                                <Box shrink={false}>
                                  <Text size="xs" variant="faint">
                                    {formatRelativeDate(row.date, "compact")}
                                  </Text>
                                </Box>
                              ) : null}
                            </Box>
                            <Box direction="row" align="center" gap="sm" minWidth={0}>
                              <MediaAddedBy userId={row.userId} />
                              <Text size="xs" variant="muted" truncate={1}>
                                {mediaOrigin(row)}
                              </Text>
                            </Box>
                          </Box>
                        }
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      )}
      <BulkActionsBar
        actions={bulkActions}
        selectedIds={selectedIds}
        onClearSelection={clear}
        selectedRowData={selectedRowData}
        selectAllGroups={NO_SELECT_ALL_GROUPS}
      />
    </Box>
  );
}
